import type { Config } from "dompurify";

// --- DOM Sanitization Config ---

export const DOM_SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS: [
    "div", "span", "a", "button", "form", "fieldset", "legend", "hr", "img", 
    "strong", "p", "br", "ul", "li", "label", "input", "section", "nav", 
    "header", "footer", "article", "aside", "table", "thead", "tbody", 
    "tr", "th", "td",
    "h1", "h2", "h3", "h4", "h5", "h6" 
  ],
  ALLOWED_ATTR: [
    "class", "id", "href", "target", "role", "aria-label", "aria-controls", 
    "aria-labelledby", "aria-expanded", "aria-hidden", "aria-describedby", 
    "aria-live", "aria-atomic", "tabindex", "type", "disabled", "src", "alt", 
    "for", "name", "value", "placeholder", "data-target", "data-toggle", "data-dismiss"
  ]
};

// --- SVG Sanitization Config ---
export const SVG_SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS: ["svg", "path", "polyline", "line", "polygon", "circle", "rect", "g"],
  ALLOWED_ATTR: [
    "viewBox", "fill", "stroke", "stroke-width", "stroke-linecap", "stroke-linejoin", 
    "d", "points", "x1", "x2", "y1", "y2", "cx", "cy", "r", "class", "xmlns"
  ]
};

// --- Combined Config ---
export const HTML_WITH_SVG_CONFIG: Config = {
  ALLOWED_TAGS: [...(DOM_SANITIZE_CONFIG.ALLOWED_TAGS ?? []), ...(SVG_SANITIZE_CONFIG.ALLOWED_TAGS ?? [])],
  ALLOWED_ATTR: [...(DOM_SANITIZE_CONFIG.ALLOWED_ATTR ?? []), ...(SVG_SANITIZE_CONFIG.ALLOWED_ATTR ?? [])]
};

// Add after HTML_WITH_SVG_CONFIG, before "Networking & Host Security"

/**
 * Umbraco Rich Text Editor output.
 * Covers everything TinyMCE can produce within Umbraco.
 * No forms, no inputs, no scripts — content display only.
 */
export const UMBRACO_RTE_SANITIZE_CONFIG: Config = {
    ALLOWED_TAGS: [
        // Block
        "p", "div", "span", "br", "hr",
        "h1", "h2", "h3", "h4", "h5", "h6",
        "blockquote", "pre", "address",

        // Inline formatting
        "strong", "b", "em", "i", "u", "s",
        "sub", "sup", "small", "mark",
        "abbr", "cite", "code", "kbd", "samp", "var",

        // Lists
        "ul", "ol", "li",
        "dl", "dt", "dd",

        // Links & media
        "a", "img",
        "figure", "figcaption",

        // Tables
        "table", "thead", "tbody", "tfoot",
        "tr", "th", "td", "caption", "colgroup", "col",
    ],
    ALLOWED_ATTR: [
        // Global
        "class", "id", "style", "title", "lang", "dir",

        // Links
        "href", "target", "rel",

        // Images
        "src", "alt", "width", "height", "loading",

        // Accessibility
        "role",
        "aria-label", "aria-labelledby", "aria-describedby",
        "aria-hidden", "aria-expanded", "aria-controls",
        "aria-live", "aria-atomic",
        "tabindex",

        // Tables
        "colspan", "rowspan", "scope", "headers",

        // Umbraco internal link tracking
        "data-udi", "data-id",
    ],
};

// --- Networking & Host Security ---
export const ALLOWED_HOSTNAMES: Set<string> = new Set([
  "", 
  "intranet.trinityclaims.co.uk", 
  "trinityclaims.co.uk"
]);

export const NON_PRODUCTION_ALLOWED_HOSTNAMES: Set<string> = new Set([
  "localhost", 
  "127.0.0.1", 
  "0.0.0.0"
]);

export const DEVELOPMENT_PORTS: Set<string> = new Set([
  "3000", "4200", "5000", "5173", "8080", "44367", "44300", "5001"
]);

export const BLOCKED_PROTOCOLS: Set<string> = new Set([
  "file:", "ftp:", "gopher:", "data:", "javascript:", "vbscript:"
]);

// --- IP & Pattern Security ---
export const BLOCKED_IP_PATTERNS: RegExp[] = [
  /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/, 
  /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/,
  /^192\.168\.\d{1,3}\.\d{1,3}$/, 
  /^169\.254\.\d{1,3}\.\d{1,3}$/, 
  /^fc00:/i, /^fd00:/i, /^fe80:/i
];

export const PRODUCTION_ONLY_BLOCKED_IP_PATTERNS: RegExp[] = [
  /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/, 
  /^localhost$/i, 
  /^::1$/, 
  /^0:0:0:0:0:0:0:1$/
];

// --- Validation Limits & Text Constants ---
export const MAX_TEXT_LENGTH = 1000;
export const MAX_DISPLAY_TEXT_LENGTH = 200;
export const MAX_ERROR_MESSAGE_LENGTH = 100;
export const DEFAULT_SEARCH_QUERY_MAX_LENGTH = 200;
export const DEFAULT_ID_MAX_LENGTH = 128;
export const MAX_EMAIL_LENGTH = 254;
export const DEFAULT_LOG_MAX_LENGTH = 200;

// --- Regular Expression Patterns ---
export const CSS_CLASS_PATTERN = /[^a-zA-Z0-9_-]/g;
export const DEFAULT_ID_PATTERN = /^[a-zA-Z0-9\-_]{1,128}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const FORMAT_SPECIFIER_PATTERN = /%[sdifjoO%]/g;